import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';
import './Vehicles.css';
import { useBranchFilter } from '../../hooks/useBranchFilter';

function Vehicles() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange, refreshTrigger } = useBranchFilter();
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [viewingVehicle, setViewingVehicle] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchVehicles();
    }
  }, [schoolData?.id, selectedBranchId, refreshTrigger]);

  useEffect(() => {
    if (selectedBranchId !== undefined) {
      setVehicles([]);
    }
  }, [selectedBranchId]);

  const fetchVehicles = async () => {
    try {
      setLoadingVehicles(true);
      let query = supabase
        .from('transport_vehicles')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: false });

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching vehicles:', error);
        alert('Error loading vehicles: ' + error.message);
        return;
      }

      setVehicles(data || []);
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while loading vehicles.');
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handleLogout = () => navigate('/schoollogin');

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewVehicle = (vehicle) => {
    setViewingVehicle(vehicle);
  };

  const handleEditVehicle = (vehicle) => {
    // Navigate to edit page
    navigate(`/school/edit-vehicle/${vehicle.id}`);
  };

  const handleDeleteVehicle = async (vehicle) => {
    if (!window.confirm(`Are you sure you want to delete vehicle "${vehicle.bus_number}"? This action cannot be undone.`)) {
      return;
    }

    try {
      let deleteQuery = supabase
        .from('transport_vehicles')
        .delete()
        .eq('id', vehicle.id);

      if (selectedBranchId != null) {
        deleteQuery = deleteQuery.eq('branch_id', selectedBranchId);
      }

      const { error } = await deleteQuery;

      if (error) {
        console.error('Error deleting vehicle:', error);
        alert('Error deleting vehicle: ' + error.message);
        return;
      }

      // Refresh the list
      fetchVehicles();
      alert('Vehicle deleted successfully!');
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while deleting the vehicle.');
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      <div className="main-content">
        <div className="vehicles-page-container">
          <div className="vehicles-container">
            {/* Page Header Card */}
            <div className="vehicles-header-card">
              <div className="vehicles-header-icon">🚌</div>
              <h1 className="vehicles-header-title">All Vehicles</h1>
              <div className="vehicles-header-subtitle">
                <span>Total Vehicles:</span>
                <span className="vehicles-count-badge">{vehicles.length}</span>
              </div>
              <div className="vehicles-add-button-container">
                <button
                  className="vehicles-add-button"
                  onClick={() => navigate('/school/add-vehicle')}
                >
                  <span className="vehicles-add-button-icon">➕</span>
                  Add Vehicle
                </button>
              </div>
            </div>

            {loadingVehicles ? (
              <div className="loading-container" style={{ padding: '2rem' }}>
                <div className="loading-spinner"></div>
                <p>Loading vehicles...</p>
              </div>
            ) : vehicles.length === 0 ? (
              <div className="vehicles-empty-state">
                <div className="vehicles-empty-icon">🚌</div>
                <p className="vehicles-empty-message">No vehicles found</p>
                <button
                  className="vehicles-empty-button"
                  onClick={() => navigate('/school/add-vehicle')}
                >
                  Add Your First Vehicle
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="vehicles-table-container">
                  <table className="vehicles-table">
                    <thead>
                      <tr>
                        <th className="vehicle-number-header">Vehicle Number</th>
                        <th className="seats-header">Number of Seats</th>
                        <th className="driver-header">Driver Name</th>
                        <th className="rules-header">Bus Rules</th>
                        <th className="actions-header">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehicles.map((vehicle) => (
                        <tr key={vehicle.id}>
                          <td>
                            <div className="vehicle-number-cell">
                              <span className="vehicle-number-icon">🚌</span>
                              <span>{vehicle.bus_number || '-'}</span>
                            </div>
                          </td>
                          <td className="seats-cell">
                            <span className="seats-badge">{vehicle.capacity || '-'}</span>
                          </td>
                          <td>
                            <div className="driver-name-cell">
                              <span className="driver-name-icon">👤</span>
                              <span>{vehicle.driver_name || '-'}</span>
                            </div>
                          </td>
                          <td>
                            <div className="bus-rules-cell">
                              {vehicle.rules_policies || '-'}
                            </div>
                          </td>
                          <td className="actions-cell">
                            <div className="vehicles-actions-container">
                              <button
                                className="vehicle-action-btn view-btn"
                                onClick={() => handleViewVehicle(vehicle)}
                                title="View Details"
                              >
                                👁️
                              </button>
                              <button
                                className="vehicle-action-btn edit-btn"
                                onClick={() => handleEditVehicle(vehicle)}
                                title="Edit Vehicle"
                              >
                                ✏️
                              </button>
                              <button
                                className="vehicle-action-btn delete-btn"
                                onClick={() => handleDeleteVehicle(vehicle)}
                                title="Delete Vehicle"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="vehicles-mobile-cards">
                  {vehicles.map((vehicle) => (
                    <div key={vehicle.id} className="vehicle-mobile-card">
                      <div className="vehicle-mobile-card-header">
                        <div className="vehicle-mobile-number">
                          <span>🚌</span>
                          <span>{vehicle.bus_number || '-'}</span>
                        </div>
                        <span className="vehicle-mobile-seats-badge">{vehicle.capacity || '-'} Seats</span>
                      </div>
                      <div className="vehicle-mobile-details">
                        <div className="vehicle-mobile-detail-row">
                          <span className="vehicle-mobile-detail-label">Driver</span>
                          <span className="vehicle-mobile-detail-value">
                            <span>👤</span>
                            <span>{vehicle.driver_name || '-'}</span>
                          </span>
                        </div>
                        <div className="vehicle-mobile-detail-row">
                          <span className="vehicle-mobile-detail-label">Bus Rules</span>
                          <span className="vehicle-mobile-detail-value" style={{ flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                            <span style={{ textAlign: 'right', maxWidth: '200px', wordWrap: 'break-word' }}>
                              {vehicle.rules_policies || '-'}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="vehicle-mobile-actions">
                        <button
                          className="vehicle-mobile-action-btn view-btn"
                          onClick={() => handleViewVehicle(vehicle)}
                        >
                          <span>👁️</span>
                          <span>View</span>
                        </button>
                        <button
                          className="vehicle-mobile-action-btn edit-btn"
                          onClick={() => handleEditVehicle(vehicle)}
                        >
                          <span>✏️</span>
                          <span>Edit</span>
                        </button>
                        <button
                          className="vehicle-mobile-action-btn delete-btn"
                          onClick={() => handleDeleteVehicle(vehicle)}
                        >
                          <span>🗑️</span>
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* View Vehicle Modal */}
            {viewingVehicle && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onClick={() => setViewingVehicle(null)}
              >
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  maxWidth: '600px',
                  width: '90%',
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <h2 style={{ margin: 0, color: '#1F2937' }}>Vehicle Details</h2>
                    <button
                      onClick={() => setViewingVehicle(null)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#6B7280'
                      }}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Vehicle Number:</strong>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.1rem', fontWeight: '600' }}>
                        {viewingVehicle.bus_number}
                      </p>
                    </div>
                    
                    {viewingVehicle.registration_no && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Registration Number:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>{viewingVehicle.registration_no}</p>
                      </div>
                    )}
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Capacity:</strong>
                      <p style={{ margin: '0.25rem 0 0 0' }}>{viewingVehicle.capacity} seats</p>
                    </div>
                    
                    {viewingVehicle.available_seats !== null && viewingVehicle.available_seats !== undefined && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Available Seats:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>{viewingVehicle.available_seats} seats</p>
                      </div>
                    )}
                    
                    {viewingVehicle.driver_name && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Driver Name:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>{viewingVehicle.driver_name}</p>
                      </div>
                    )}
                    
                    {viewingVehicle.driver_phone && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Driver Phone:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>{viewingVehicle.driver_phone}</p>
                      </div>
                    )}
                    
                    {viewingVehicle.assistant_name && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Assistant Name:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>{viewingVehicle.assistant_name}</p>
                      </div>
                    )}
                    
                    {viewingVehicle.assistant_phone && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Assistant Phone:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>{viewingVehicle.assistant_phone}</p>
                      </div>
                    )}
                    
                    {viewingVehicle.rules_policies && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Bus Rules and Policies:</strong>
                        <p style={{ margin: '0.25rem 0 0 0', whiteSpace: 'pre-wrap' }}>{viewingVehicle.rules_policies}</p>
                      </div>
                    )}
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Status:</strong>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          backgroundColor: viewingVehicle.is_active ? '#D1FAE5' : '#FEE2E2',
                          color: viewingVehicle.is_active ? '#065F46' : '#991B1B',
                          marginLeft: '0.5rem'
                        }}
                      >
                        {viewingVehicle.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Added Date:</strong>
                      <p style={{ margin: '0.25rem 0 0 0' }}>{formatDate(viewingVehicle.created_at)}</p>
                    </div>
                  </div>
                  
                  <div style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={() => {
                        setViewingVehicle(null);
                        handleEditVehicle(viewingVehicle);
                      }}
                      style={{
                        background: '#10B981',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setViewingVehicle(null)}
                      style={{
                        background: '#6B7280',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Vehicles;

